"use client"

import { SolveForWidget, type SolveForField } from "./solve-for-widget"

/**
 * CTR = clicks ÷ impressions × 100 — solve for any one of the three.
 *
 * CTR is held as a percentage (1.5 means 1.5%), because that is how every ad
 * platform reports it and how people type it. Default example: 1,200 clicks
 * from 80,000 impressions = 1.5% CTR — arithmetic, not a benchmark.
 */

type CtrKey = "ctr" | "clicks" | "impressions"

const formatCount = (n: number) =>
  Number.isFinite(n) ? Math.round(n).toLocaleString("en-US") : "—"

const formatCtr = (pct: number) =>
  Number.isFinite(pct)
    ? `${pct.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`
    : "—"

const fields = [
  {
    key: "ctr",
    tabLabel: "CTR",
    label: "Click-through rate",
    resultLabel: "Click-through rate",
    defaultValue: "1.5",
    suffix: "%",
    format: formatCtr,
  },
  {
    key: "clicks",
    tabLabel: "Clicks",
    label: "Clicks",
    resultLabel: "Expected clicks",
    defaultValue: "1200",
    step: 1,
    format: formatCount,
  },
  {
    key: "impressions",
    tabLabel: "Impressions",
    label: "Impressions",
    resultLabel: "Impressions needed",
    defaultValue: "80000",
    step: 1,
    format: (n) => (Number.isFinite(n) ? Math.ceil(n).toLocaleString("en-US") : "—"),
  },
] as const satisfies readonly [
  SolveForField<CtrKey>,
  SolveForField<CtrKey>,
  SolveForField<CtrKey>,
]

export function CtrWidget() {
  return (
    <SolveForWidget<CtrKey>
      title="CTR Calculator"
      idPrefix="ctr"
      fields={fields}
      solve={(target, { ctr, clicks, impressions }) => {
        if (target === "ctr") {
          if (clicks === null || impressions === null) return null
          if (clicks < 0 || impressions <= 0) return null
          const value = (clicks / impressions) * 100
          if (value > 100) {
            return {
              value,
              tone: "bad",
              caption:
                "More clicks than impressions. The two numbers almost certainly come from different reports — a different date range, a different filter, or an all-clicks count set against a link-click denominator.",
            }
          }
          return {
            value,
            caption:
              clicks > 0
                ? `${formatCount(clicks)} clicks from ${formatCount(impressions)} impressions — roughly one click for every ${formatCount(impressions / clicks)} times the ad was shown.`
                : "No clicks yet. Give the ad enough impressions before reading anything into a zero.",
          }
        }
        if (target === "clicks") {
          if (ctr === null || impressions === null) return null
          if (ctr < 0 || ctr > 100 || impressions < 0) return null
          const value = (ctr / 100) * impressions
          return {
            value,
            caption: `At a ${formatCtr(ctr)} CTR, ${formatCount(impressions)} impressions should return about ${formatCount(value)} clicks. Expect your site analytics to record fewer sessions than the platform records clicks.`,
          }
        }
        if (clicks === null || ctr === null) return null
        if (clicks < 0 || ctr <= 0 || ctr > 100) return null
        const value = clicks / (ctr / 100)
        return {
          value,
          caption: `To get ${formatCount(clicks)} clicks at a ${formatCtr(ctr)} CTR, the ad has to be shown about ${Math.ceil(value).toLocaleString("en-US")} times. Price those impressions with your CPM to get the budget.`,
        }
      }}
      invalidCaption={(target) =>
        target === "ctr"
          ? "Enter clicks and a non-zero impression count."
          : target === "clicks"
            ? "Enter a CTR between 0 and 100% and an impression count."
            : "Enter a click goal and a CTR above 0%."
      }
      formulaNote={(target) =>
        target === "ctr"
          ? "CTR = Clicks ÷ Impressions × 100. Both numbers must use the same click definition — link clicks against impressions is not the same metric as all clicks against impressions."
          : target === "clicks"
            ? "Clicks = Impressions × CTR ÷ 100."
            : "Impressions = Clicks ÷ (CTR ÷ 100)."
      }
    />
  )
}
